import cn from 'classnames';
import React from 'react';
import {connect, useSelector} from 'react-redux';
import {Route} from 'react-router-dom';
import {createStructuredSelector} from 'reselect';

import appConfig from '../../config/appConfig';
import domClassNames from '../../consts/app/domClassNames';
import useFirmwareReleaseNotesBanner from '../../hooks/firmwareReleaseNotes/useFirmwareReleaseNotesBanner';
import useFwuBackgroundCheck from '../../hooks/fwu/useFwuBackgroundCheck';
import {useHidReconnectHidden} from '../../hooks/hid/useHidReconnectHandler';
import useHistoryScrollTop from '../../hooks/useHistoryScrollTop';
import useListenBluetoothAvailabilityChange from '../../hooks/useListenBluetoothAvailabilityChange';
import rtlService from '../../services/app/rtlService';
import connectivityService from '../../services/device/connectivityService';
import {getInitialLanguageCode} from '../../services/localization/localizationService';
import userDeviceService from '../../services/user/userDeviceService';
import {selectDoesNavigationTargetExist} from '../../state/selectors/global';
import {selectIsTncPopupVisible} from '../../state/selectors/tnc';
import Accessibility from '../Accessibility/Accessibility';
import LoaderGlobal from '../LoaderGlobal/LoaderGlobal';
import ModalsRoot from '../Modals/ModalsRoot';
import Notifications from '../Notifications/Notifications';
import ProductRegistrationStatus from '../ProductRegistrationStatus/ProductRegistrationStatus';
import ConsumerSettingsChecker from '../ServiceContainers/ConsumerSettingsChecker/ConsumerSettingsChecker';
import YapPairingChecker from '../ServiceContainers/YapPairingChecker/YapPairingChecker';
import YapRequestStatusChecker from '../ServiceContainers/YapRequestStatusChecker/YapRequestStatusChecker';
import TermsOfUsePopup from '../TNC/TermsOfUsePopup/TermsOfUsePopup';
import AemFlexBatterySaveWarningHider from './components/AemFlexBatterySaveWarningHider';
import DataCollectionDiagnosticHandler from './components/DataCollection/DataCollectionDiagnosticHandler';
import DataCollectionHandler from './components/DataCollection/DataCollectionHandler';
import DataCollectionUsageHandler from './components/DataCollection/DataCollectionUsageHandler';
import DeviceConnectionChecker from './components/DeviceConnectionChecker';
import DeviceConnectionDetector from './components/DeviceConnectionDetector';
import DeviceHolderConnectionChecker from './components/DeviceHolderConnectionChecker';
import DeviceHolderRegistrationChecker from './components/DeviceHolderRegistrationChecker';
import DeviceManualSyncChecker from './components/DeviceManualSyncChecker';
import NavigationTargetRedirector from './components/NavigationTargetRedirector';
import NotCompatibleOSPopup from './components/NotCompatibleOSPopup';
import OfflineDetector from './components/OfflineDetector';
import YapOfflineDetector from './components/YapOfflineDetector';
import useAmplitudeInit from './hooks/amplitude/useAmplitudeInit';
import useDeviceIdlePingSchedulerPause from './hooks/useDeviceIdlePingSchedulerPause';
import useIotDeviceConfigReset from './hooks/useIotDeviceConfigReset';
import usePageContentInert from './hooks/usePageContentInert';
import Logs from './Logs/Logs';

const mapStateToProps = createStructuredSelector({
    isTncPopupVisible: selectIsTncPopupVisible,
});

const AppLayout = (props) => {
    const {component: Component, path, isTncPopupVisible} = props;
    const isNotCompatibleVersion = userDeviceService.isIos_13_4_x();
    const isTouchSupported = userDeviceService.isTouchSupported();
    const isBle = connectivityService.isBle();
    const isRTLEnabled = rtlService.isRtlEnabled();
    const languageCode = getInitialLanguageCode()?.toLowerCase();
    const doesNavigationTargetExist = useSelector(selectDoesNavigationTargetExist);
    const pageContentRef = usePageContentInert();

    useIotDeviceConfigReset();
    useHistoryScrollTop();
    useFwuBackgroundCheck();
    useFirmwareReleaseNotesBanner();
    useDeviceIdlePingSchedulerPause();
    useListenBluetoothAvailabilityChange();
    useHidReconnectHidden();
    useAmplitudeInit();

    return (
        <Route
            path={path}
            render={(matchProps) => (
                <div
                    dir={isRTLEnabled ? 'rtl' : 'ltr'}
                    className={cn(
                        'relative flex flex-col items-center bg-romance font-iqos',
                        domClassNames.APP_CLASSNAME,
                        appConfig.isStandaloneMarket() ? 'min-h-[100vh]' : 'min-h-full',
                        {
                            touch: isTouchSupported,
                        },
                        languageCode
                    )}
                >
                    <Accessibility />
                    <Notifications />
                    {isTncPopupVisible && <TermsOfUsePopup matchProps={matchProps} />}
                    <div className={cn('min-h-[568px] w-full', domClassNames.COMPONENTS_CONTAINER_CLASS_NAME)}>
                        <div ref={pageContentRef} className='page-content flex justify-center'>
                            <Component {...matchProps} />
                        </div>
                    </div>
                    <LoaderGlobal />
                    <ModalsRoot />
                    {isNotCompatibleVersion ? (
                        <NotCompatibleOSPopup />
                    ) : (
                        <>
                            <ProductRegistrationStatus />
                            <ProductRegistrationStatus isHolder />
                            <ConsumerSettingsChecker />
                        </>
                    )}
                    {(appConfig.getIsLogsVisible() || appConfig.getIsAppVersionVisible()) && <Logs />}
                    <OfflineDetector />
                    {isBle && <DeviceConnectionDetector />}
                    <YapPairingChecker />
                    <YapRequestStatusChecker />
                    <YapOfflineDetector />
                    <DataCollectionHandler />
                    <DataCollectionUsageHandler />
                    <DataCollectionDiagnosticHandler />
                    <DeviceHolderRegistrationChecker />
                    <DeviceHolderConnectionChecker />
                    <DeviceConnectionChecker />
                    <DeviceManualSyncChecker />
                    <AemFlexBatterySaveWarningHider />
                    {doesNavigationTargetExist && <NavigationTargetRedirector />}
                </div>
            )}
        />
    );
};

export default connect(mapStateToProps)(AppLayout);
